/**
 * Custom Header Component
 * Handles mobile drawer menu, currency selector, and interactions
 */

class CustomHeader extends HTMLElement {
  constructor() {
    super();
    console.log('CustomHeader initialized');
    // Elements
    this.hamburgers = this.querySelectorAll('.custom-header__hamburger');
    this.drawer = this.querySelector('.custom-header__drawer');
    this.drawerOverlay = this.querySelector('.custom-header__drawer-overlay');
    this.drawerClose = this.querySelector('.custom-header__drawer-close');
    this.currencyButton = this.querySelector('.custom-header__currency-button');
    this.currencyDropdown = this.querySelector('.custom-header__currency-dropdown');
    this.submenuToggles = this.querySelectorAll('.custom-header__drawer-submenu-toggle');

    this.init();
  }

  init() {
    this.setupMobileMenu();
    this.setupCurrencySelector();
    this.setupSubmenus();
    this.setupKeyboardNav();
    this.setupAnnouncementBarScroll();
  }

  /* ==========================================================================
     Announcement Bar Hide on Scroll (Mobile Only)
     ========================================================================== */

  setupAnnouncementBarScroll() {
    this.announcementBar = this.querySelector('.custom-header__announcement-bar');
    if (!this.announcementBar) return;

    let lastScrollY = 0;
    let ticking = false;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Only on mobile (max-width: 990px)
      if (window.innerWidth <= 990) {
        if (currentScrollY > 50) {
          this.announcementBar.classList.add('is-hidden');
        } else {
          this.announcementBar.classList.remove('is-hidden');
        }
      } else {
        // Always visible on desktop
        this.announcementBar.classList.remove('is-hidden');
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(handleScroll);
        ticking = true;
      }
    }, { passive: true });

    // Also handle resize
    window.addEventListener('resize', () => {
      if (window.innerWidth > 990) {
        this.announcementBar.classList.remove('is-hidden');
      }
    }, { passive: true });
  }

  /* ==========================================================================
     Mobile Drawer Menu
     ========================================================================== */

  setupMobileMenu() {
    //if (!this.hamburgers.length || !this.drawer) return;
    console.log('setup mobile menu');
    // Open drawer - attach to ALL hamburger buttons
    this.hamburgers.forEach(hamburger => {
      hamburger.addEventListener('click', () => this.openDrawer());
    });

    // Close drawer
    this.drawerClose?.addEventListener('click', () => this.closeDrawer());
    this.drawerOverlay?.addEventListener('click', () => this.closeDrawer());

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.drawer.hidden) {
        this.closeDrawer();
      }
    });
  }

  openDrawer() {
    if (!this.drawer) return;
    console.log('open');
    this.drawer.hidden = false;
    this.hamburgers.forEach(hamburger => {
      hamburger.setAttribute('aria-expanded', 'true');
    });
    document.body.classList.add('drawer-open');

    // Focus trap
    this.trapFocus();

    // Announce to screen readers
    this.announceToScreenReader('Menu otwarte');
  }

  closeDrawer() {
    if (!this.drawer) return;

    this.drawer.hidden = true;
    this.hamburgers.forEach(hamburger => {
      hamburger.setAttribute('aria-expanded', 'false');
    });
    document.body.classList.remove('drawer-open');

    // Return focus to first visible hamburger
    const visibleHamburger = Array.from(this.hamburgers).find(h => h.offsetParent !== null);
    if (visibleHamburger) visibleHamburger.focus();

    // Announce to screen readers
    this.announceToScreenReader('Menu zamknięte');
  }

  /* ==========================================================================
     Currency Selector
     ========================================================================== */

  setupCurrencySelector() {
    if (!this.currencyButton || !this.currencyDropdown) return;

    // Toggle dropdown
    this.currencyButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleCurrencyDropdown();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.currencyDropdown.hidden &&
          !this.currencyDropdown.contains(e.target) &&
          e.target !== this.currencyButton) {
        this.closeCurrencyDropdown();
      }
    });

    // Handle currency selection
    const currencyOptions = this.currencyDropdown.querySelectorAll('.custom-header__currency-option');
    currencyOptions.forEach(option => {
      option.addEventListener('click', () => {
        this.closeCurrencyDropdown();
      });
    });
  }

  toggleCurrencyDropdown() {
    const isHidden = this.currencyDropdown.hidden;

    if (isHidden) {
      this.currencyDropdown.hidden = false;
      this.currencyButton.setAttribute('aria-expanded', 'true');
    } else {
      this.closeCurrencyDropdown();
    }
  }

  closeCurrencyDropdown() {
    this.currencyDropdown.hidden = true;
    this.currencyButton.setAttribute('aria-expanded', 'false');
  }

  /* ==========================================================================
     Submenu Toggles (Mobile Drawer)
     ========================================================================== */

  setupSubmenus() {
    this.submenuToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggleSubmenu(toggle);
      });
    });
  }

  toggleSubmenu(toggle) {
    const menuItem = toggle.closest('.custom-header__drawer-menu-item');
    const submenu = menuItem.querySelector('.custom-header__drawer-submenu');

    if (!submenu) return;

    const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      submenu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
    } else {
      // Close other open submenus
      this.submenuToggles.forEach(otherToggle => {
        if (otherToggle !== toggle) {
          const otherMenuItem = otherToggle.closest('.custom-header__drawer-menu-item');
          const otherSubmenu = otherMenuItem.querySelector('.custom-header__drawer-submenu');
          if (otherSubmenu) {
            otherSubmenu.hidden = true;
            otherToggle.setAttribute('aria-expanded', 'false');
          }
        }
      });

      // Open this submenu
      submenu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }
  }

  /* ==========================================================================
     Keyboard Navigation
     ========================================================================== */

  setupKeyboardNav() {
    // Escape key handling is in setupMobileMenu

    // Tab trap in drawer
    if (this.drawer) {
      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          this.handleTabKey(e);
        }
      });
    }
  }

  handleTabKey(e) {
    const focusableElements = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  /* ==========================================================================
     Focus Trap
     ========================================================================== */

  trapFocus() {
    const focusableElements = this.drawer.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (focusableElements.length > 0) {
      // Focus first element after a short delay for animation
      setTimeout(() => {
        focusableElements[0].focus();
      }, 100);
    }
  }

  /* ==========================================================================
     Accessibility Announcements
     ========================================================================== */

  announceToScreenReader(message) {
    // Create or get announcement element
    let announcer = document.getElementById('header-announcer');

    if (!announcer) {
      announcer = document.createElement('div');
      announcer.id = 'header-announcer';
      announcer.setAttribute('role', 'status');
      announcer.setAttribute('aria-live', 'polite');
      announcer.setAttribute('aria-atomic', 'true');
      announcer.style.position = 'absolute';
      announcer.style.left = '-10000px';
      announcer.style.width = '1px';
      announcer.style.height = '1px';
      announcer.style.overflow = 'hidden';
      document.body.appendChild(announcer);
    }

    // Clear and set new message
    announcer.textContent = '';
    setTimeout(() => {
      announcer.textContent = message;
    }, 100);
  }
}

// Register custom element
if (!customElements.get('custom-header')) {
  customElements.define('custom-header', CustomHeader);
}

/* ==========================================================================
   Cart Count Update (for dynamic cart)
   ========================================================================== */

// Listen for cart updates
document.addEventListener('cart:updated', (event) => {
  const cartCount = event.detail?.item_count || 0;
  updateCartCount(cartCount);
});

function updateCartCount(count) {
  const cartIcons = document.querySelectorAll('.custom-header__cart');

  cartIcons.forEach(cartIcon => {
    let countElement = cartIcon.querySelector('.custom-header__cart-count');

    if (count > 0) {
      if (!countElement) {
        countElement = document.createElement('span');
        countElement.className = 'custom-header__cart-count';
        cartIcon.appendChild(countElement);
      }
      countElement.textContent = count;
    } else {
      if (countElement) {
        countElement.remove();
      }
    }
  });
}

// Initialize cart count on page load
if (typeof fetch !== 'undefined') {
  fetch('/cart.js')
    .then(response => response.json())
    .then(cart => {
      updateCartCount(cart.item_count);
    })
    .catch(error => {
      console.warn('Could not fetch cart data:', error);
    });
}
